import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatError, fmtPrice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const KIND = { 0: "Unit", 1: "Accessory", 2: "Part" };
import { toast } from "sonner";
import { Search, ImageIcon, Paperclip, Ghost, Trash2, UploadCloud, X, Archive, ArchiveRestore, Ban } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unclassified", label: "Unclassified" },
  { id: "classified", label: "Classified" },
  { id: "units", label: "Units" },
  { id: "accessories", label: "Accessories" },
  { id: "parts", label: "Parts" },
  { id: "orphaned", label: "Orphaned" },
  { id: "archived", label: "Archived" },
];

function AuthImg({ refId, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let active = true;
    api.get(`/admin/files/${refId}`, { responseType: "blob" }).then((r) => {
      if (!active) return;
      url = URL.createObjectURL(r.data);
      setSrc(url);
    }).catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [refId]);
  if (!src) return <div className={`${className} bg-slate-100 animate-pulse`} />;
  return <img src={src} alt="" className={className} />;
}

function AssetDialog({ product, families, onClose, onChanged }) {
  const [p, setP] = useState(product);
  const imgRef = useRef();
  const attRef = useRef();
  const [attFile, setAttFile] = useState(null);
  const [attName, setAttName] = useState("");
  const [desc, setDesc] = useState(product.description || "");
  const [notes, setNotes] = useState(product.notes || "");

  const saveNotes = async () => {
    try {
      const { data } = await api.put(`/admin/products/notes?sku=${encodeURIComponent(p.sku)}`, { notes });
      setP(data); setNotes(data.notes || ""); onChanged?.();
      toast.success("Notes updated");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const setArchived = async (archived) => {
    let reason = "";
    if (archived) {
      reason = window.prompt("Why is this product being archived? (shown to staff only)", "");
      if (reason === null) return;
    }
    try {
      const { data } = await api.put(`/admin/products/archive?sku=${encodeURIComponent(p.sku)}`, { archived, reason });
      setP(data); onChanged?.();
      toast.success(archived ? "Product archived — hidden from customers" : "Product restored");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const deleteProduct = async () => {
    if (!window.confirm(`Permanently remove ${p.sku}? It will be blocked from being re-added by future price imports (you can unblock it later from "Blocked SKUs").`)) return;
    try {
      await api.delete(`/admin/products?sku=${encodeURIComponent(p.sku)}`);
      toast.success(`${p.sku} removed and blocked`);
      onChanged?.(); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const reload = async () => {
    const { data } = await api.get(`/admin/products?search=${encodeURIComponent(p.sku)}`);
    const found = data.find((x) => x.sku === p.sku);
    if (found) setP(found);
    onChanged?.();
  };

  const toggleFamily = async (fam) => {
    const has = p.families.includes(fam);
    const next = has ? p.families.filter((f) => f !== fam) : [...p.families, fam];
    try {
      const { data } = await api.put(`/admin/products/families?sku=${encodeURIComponent(p.sku)}`, { families: next });
      setP(data);
      onChanged?.();
    } catch (e) { toast.error("Could not update families"); }
  };

  const setKind = async (k) => {
    try {
      const { data } = await api.put(`/admin/products/kind?sku=${encodeURIComponent(p.sku)}`, { kind: parseInt(k, 10) });
      setP(data);
      onChanged?.();
    } catch (e) { toast.error("Could not update type"); }
  };

  const upImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/products/image?sku=${encodeURIComponent(p.sku)}`, fd); toast.success("Image updated"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const delImage = async () => { await api.delete(`/admin/products/image?sku=${encodeURIComponent(p.sku)}`); toast.success("Image removed"); reload(); };
  const upAtt = (file) => { if (file) { setAttFile(file); setAttName(file.name); } };
  const confirmAtt = async () => {
    const f = attFile, name = attName; setAttFile(null);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/admin/products/attachments?sku=${encodeURIComponent(p.sku)}&name=${encodeURIComponent(name)}`, fd); toast.success("Attachment added"); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const delAtt = async (refId) => { await api.delete(`/admin/attachments/${refId}`); toast.success("Attachment removed"); reload(); };

  const saveDesc = async () => {
    try {
      const { data } = await api.put(`/admin/products/description?sku=${encodeURIComponent(p.sku)}`, { description: desc });
      setP(data); setDesc(data.description || ""); onChanged?.();
      toast.success("Description updated");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const revertDesc = async () => {
    try {
      const { data } = await api.put(`/admin/products/description?sku=${encodeURIComponent(p.sku)}`, { description: "" });
      setP(data); setDesc(data.description || ""); onChanged?.();
      toast.success("Reverted to imported description");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-auto" data-testid="asset-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-mono text-lg flex items-center gap-2">
            {p.sku}
            {p.archived ? <span className="text-[10px] font-semibold font-sans text-slate-600 bg-slate-200 px-1.5 py-0.5 rounded-sm" data-testid="archived-badge">Archived</span> : null}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">Manage description, families, image and attachments for this SKU.</DialogDescription>
        {p.archived ? (
          <div className="-mt-2 mb-1 rounded-sm bg-slate-100 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="archived-info">
            <b>Archived</b>{p.archived_by ? ` by ${p.archived_by}` : ""}{p.archived_at ? ` on ${new Date(p.archived_at).toLocaleDateString()}` : ""}
            {p.archived_reason ? <> — <span className="italic">{p.archived_reason}</span></> : <span className="text-slate-400"> — no reason given</span>}
          </div>
        ) : null}
        <div className="-mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Description</Label>
            {p.description_override ? <span className="text-[10px] font-semibold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-sm">Custom</span> : null}
          </div>
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="rounded-sm resize-y text-sm" data-testid="product-description-input" />
          <div className="flex items-center gap-2">
            <Button size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={saveDesc} disabled={desc.trim() === (p.description || "").trim()} data-testid="save-description-button">Save description</Button>
            {p.description_override ? <Button size="sm" variant="ghost" className="rounded-sm text-slate-500 hover:text-slate-700" onClick={revertDesc} data-testid="revert-description-button">Revert to imported</Button> : null}
          </div>
          {p.description_override && p.imported_description ? <p className="text-xs text-slate-400">Imported: {p.imported_description}</p> : null}
        </div>

        <div className="mt-3 space-y-2">
          <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Notes · shown in smaller text under the description</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} placeholder="e.g. Non-stock item — allow 2–3 weeks lead time" className="rounded-sm resize-y text-xs" data-testid="product-notes-input" />
          <Button size="sm" variant="outline" className="rounded-sm" onClick={saveNotes} disabled={notes.trim() === (p.notes || "").trim()} data-testid="save-notes-button">Save notes</Button>
        </div>

        <div className="mt-2">
          <div className="text-xs uppercase tracking-[0.1em] text-slate-500 mb-2">Families</div>
          <div className="flex flex-wrap gap-2">
            {families.map((fam) => (
              <label key={fam} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border cursor-pointer text-sm transition-colors ${
                p.families.includes(fam) ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 hover:bg-slate-50"
              }`} data-testid={`family-toggle-${fam.replace(/\s+/g,'-')}`}>
                <Checkbox checked={p.families.includes(fam)} onCheckedChange={() => toggleFamily(fam)} />
                {fam}
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-[0.1em] text-slate-500 mb-2">Type · used for shipping</div>
          <Select value={String(p.kind ?? 0)} onValueChange={setKind}>
            <SelectTrigger className="rounded-sm w-48" data-testid="kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Unit</SelectItem>
              <SelectItem value="1">Accessory</SelectItem>
              <SelectItem value="2">Part</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-4 grid gap-2">
          <div className="text-xs uppercase tracking-[0.1em] text-slate-500">Prices by level</div>
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(p.prices).map(([lvl, amt]) => (
                  <tr key={lvl} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-1.5 text-slate-600">{lvl}</td>
                    <td className="px-3 py-1.5 text-right font-medium mono-nums">{fmtPrice(amt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.1em] text-slate-500 mb-2">Product image</div>
            {p.image_ref ? (
              <div className="relative w-full aspect-square border border-slate-200 rounded-sm overflow-hidden">
                <AuthImg refId={p.image_ref} className="w-full h-full object-cover" />
                <button onClick={delImage} className="absolute top-2 right-2 bg-white/90 border border-slate-200 rounded-sm p-1 hover:bg-red-50" data-testid="delete-image">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </button>
              </div>
            ) : (
              <div className="w-full aspect-square border border-dashed border-slate-300 rounded-sm grid place-items-center text-slate-400">
                <ImageIcon className="h-8 w-8" />
              </div>
            )}
            <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => upImage(e.target.files[0])} data-testid="image-input" />
            <Button variant="outline" size="sm" className="w-full mt-2 rounded-sm" onClick={() => imgRef.current?.click()} data-testid="upload-image-button">
              <UploadCloud className="h-4 w-4 mr-2" /> {p.image_ref ? "Replace image" : "Upload image"}
            </Button>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.1em] text-slate-500 mb-2">Attachments</div>
            <div className="space-y-1.5">
              {p.attachments.length === 0 && <div className="text-sm text-slate-400">No attachments</div>}
              {p.attachments.map((a) => (
                <div key={a.ref_id} className="flex items-center gap-2 text-sm border border-slate-200 rounded-sm px-2 py-1.5">
                  <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                  <span className="flex-1 truncate">{a.filename}</span>
                  <button onClick={() => delAtt(a.ref_id)} className="text-red-500 hover:text-red-700" data-testid={`delete-att-${a.ref_id}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={attRef} type="file" accept=".pdf,image/*" hidden onChange={(e) => upAtt(e.target.files[0])} data-testid="attachment-input" />
            <Dialog open={!!attFile} onOpenChange={(o) => !o && setAttFile(null)}>
              <DialogContent className="rounded-sm" data-testid="rename-attachment-dialog">
                <DialogHeader><DialogTitle className="font-heading">Name this file</DialogTitle></DialogHeader>
                <DialogDescription className="sr-only">Display name customers will see.</DialogDescription>
                <Input value={attName} onChange={(e) => setAttName(e.target.value)} className="rounded-sm" data-testid="rename-attachment-input" />
                <DialogFooter>
                  <Button variant="outline" className="rounded-sm" onClick={() => setAttFile(null)}>Cancel</Button>
                  <Button className="rounded-sm bg-slate-900" onClick={confirmAtt} data-testid="rename-attachment-confirm">Upload</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button variant="outline" size="sm" className="w-full mt-2 rounded-sm" onClick={() => attRef.current?.click()} data-testid="upload-attachment-button">
              <Paperclip className="h-4 w-4 mr-2" /> Add attachment
            </Button>
          </div>
        </div>

        <div className="mt-6 border-t border-slate-200 pt-4 flex flex-wrap items-center gap-2">
          <div className="text-xs uppercase tracking-[0.1em] text-slate-500 mr-auto">Visibility</div>
          {p.archived ? (
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setArchived(false)} data-testid="restore-product-button"><ArchiveRestore className="h-4 w-4 mr-2" /> Restore to price list</Button>
          ) : (
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setArchived(true)} data-testid="archive-product-button"><Archive className="h-4 w-4 mr-2" /> Archive (hide from customers)</Button>
          )}
          <Button size="sm" variant="outline" className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={deleteProduct} data-testid="delete-product-button"><Trash2 className="h-4 w-4 mr-2" /> Remove & block</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BlockedSkusDialog({ open, onClose, onChanged }) {
  const [skus, setSkus] = useState([]);
  useEffect(() => { if (open) api.get("/admin/blocked-skus").then((r) => setSkus(r.data.skus)); }, [open]);
  const unblock = async (sku) => {
    try { const { data } = await api.post("/admin/blocked-skus/unblock", { sku }); setSkus(data.skus); toast.success(`${sku} will be re-added on the next import`); onChanged?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="blocked-skus-dialog">
        <DialogHeader><DialogTitle className="font-heading">Blocked SKUs</DialogTitle></DialogHeader>
        <DialogDescription>Removed products that future price imports will skip. Unblock one to let the next import add it back.</DialogDescription>
        <div className="max-h-80 overflow-auto divide-y divide-slate-100">
          {skus.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No blocked SKUs.</div>}
          {skus.map((s) => (
            <div key={s} className="flex items-center gap-2 py-2">
              <span className="flex-1 font-mono text-sm">{s}</span>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => unblock(s)} data-testid={`unblock-${s}`}>Unblock</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductsPanel({ onChange }) {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [families, setFamilies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(new Set());
  const [bulkFam, setBulkFam] = useState("");
  const [bulkKind, setBulkKind] = useState("");
  const bulkRef = useRef();
  const bulkAttRef = useRef();
  const [bulkAttFile, setBulkAttFile] = useState(null);
  const [bulkAttName, setBulkAttName] = useState("");
  const [libOpen, setLibOpen] = useState(false);
  const [libItems, setLibItems] = useState([]);
  const [comboOpen, setComboOpen] = useState(false);
  const [comboImg, setComboImg] = useState(null);
  const [comboDoc, setComboDoc] = useState(null);
  const [comboDocName, setComboDocName] = useState("");
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [bulkNotesText, setBulkNotesText] = useState("");

  const bulkArchive = async (archived) => {
    if (!window.confirm(`${archived ? "Archive" : "Restore"} ${checked.size} SKU${checked.size === 1 ? "" : "s"}? ${archived ? "They will be hidden from customers." : "They will be visible to customers again."}`)) return;
    let reason = "";
    if (archived) {
      reason = window.prompt("Reason for archiving (shown to staff only, optional):", "");
      if (reason === null) return;
    }
    try {
      const { data } = await api.post("/admin/bulk-archive", { skus: [...checked], archived, reason });
      toast.success(`${archived ? "Archived" : "Restored"} ${data.affected} SKU${data.affected === 1 ? "" : "s"}`);
      setChecked(new Set()); load(); onChange?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const bulkNotes = async () => {
    try {
      const { data } = await api.post("/admin/bulk-notes", { skus: [...checked], notes: bulkNotesText });
      toast.success(`${bulkNotesText.trim() ? "Note set on" : "Notes cleared on"} ${data.affected} SKU${data.affected === 1 ? "" : "s"}`);
      setNotesOpen(false); setBulkNotesText(""); setChecked(new Set()); load(); onChange?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const allVisibleSelected = products.length > 0 && products.every((p) => checked.has(p.sku));
  const toggleAll = () => setChecked(allVisibleSelected ? new Set() : new Set(products.map((p) => p.sku)));
  const toggleOne = (sku) => {
    const n = new Set(checked);
    n.has(sku) ? n.delete(sku) : n.add(sku);
    setChecked(n);
  };
  const bulkApply = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("skus", JSON.stringify([...checked]));
    try {
      const { data } = await api.post("/admin/bulk-image", fd);
      toast.success(`Image applied to ${data.applied} SKU${data.applied === 1 ? "" : "s"}`);
      setChecked(new Set());
      load();
      onChange?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { if (bulkRef.current) bulkRef.current.value = ""; }
  };

  const bulkAssignFamily = async () => {
    if (!bulkFam) { toast.error("Pick a family first"); return; }
    try {
      const { data } = await api.post("/admin/bulk-families", { skus: [...checked], family: bulkFam, action: "add" });
      toast.success(`Added ${bulkFam} to ${data.affected} SKU${data.affected === 1 ? "" : "s"}`);
      setChecked(new Set()); setBulkFam(""); load(); onChange?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const bulkSetKind = async () => {
    if (bulkKind === "") { toast.error("Pick a type first"); return; }
    try {
      const { data } = await api.post("/admin/bulk-kind", { skus: [...checked], kind: parseInt(bulkKind, 10) });
      toast.success(`Set type on ${data.affected} SKU${data.affected === 1 ? "" : "s"}`);
      setChecked(new Set()); setBulkKind(""); load(); onChange?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const bulkAttach = async () => {
    const f = bulkAttFile, name = bulkAttName; setBulkAttFile(null);
    const fd = new FormData(); fd.append("file", f); fd.append("skus", JSON.stringify([...checked])); fd.append("name", name);
    try { const { data } = await api.post("/admin/bulk-attachment", fd); toast.success(`Attached to ${data.affected} SKU${data.affected === 1 ? "" : "s"}`); setChecked(new Set()); load(); onChange?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { if (bulkAttRef.current) bulkAttRef.current.value = ""; }
  };

  const openLibrary = async () => {
    try { const { data } = await api.get("/admin/attachment-library"); setLibItems(data); setLibOpen(true); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const attachExisting = async (item) => {
    try {
      const { data } = await api.post("/admin/bulk-attach-existing", { skus: [...checked], storage_path: item.storage_path, filename: item.filename, content_type: item.content_type, size: item.size });
      toast.success(`Attached ${item.filename} to ${data.affected} SKU${data.affected === 1 ? "" : "s"}`);
      setLibOpen(false); setChecked(new Set()); load(); onChange?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const applyCombo = async () => {
    if (!comboImg && !comboDoc) { toast.error("Pick an image and/or a document"); return; }
    try {
      const msg = [];
      if (comboImg) { const fd = new FormData(); fd.append("file", comboImg); fd.append("skus", JSON.stringify([...checked])); const { data } = await api.post("/admin/bulk-image", fd); msg.push(`image → ${data.applied}`); }
      if (comboDoc) { const fd = new FormData(); fd.append("file", comboDoc); fd.append("skus", JSON.stringify([...checked])); fd.append("name", comboDocName || comboDoc.name); const { data } = await api.post("/admin/bulk-attachment", fd); msg.push(`doc → ${data.affected}`); }
      toast.success(`Applied (${msg.join(", ")})`);
      setComboOpen(false); setComboImg(null); setComboDoc(null); setComboDocName(""); setChecked(new Set()); load(); onChange?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/products?filter=${filter}&search=${encodeURIComponent(search)}`)
      .then((r) => { setProducts(r.data); setChecked(new Set()); })
      .finally(() => setLoading(false));
  }, [filter, search]);

  useEffect(() => { api.get("/admin/meta").then((r) => setFamilies(r.data.families)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div>
      <header className="mb-6">
        <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Products & Mapping</h1>
        <p className="text-slate-500 mt-1">Assign SKUs to families, upload images and attachments. Assets persist across imports.</p>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-white border border-slate-200 rounded-sm p-1">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                filter === f.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}>{f.label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SKU or description"
            className="pl-9 rounded-sm" data-testid="product-search" />
        </div>
        <Button variant="outline" size="sm" className="rounded-sm ml-auto" onClick={() => setBlockedOpen(true)} data-testid="blocked-skus-button"><Ban className="h-4 w-4 mr-2" /> Blocked SKUs</Button>
      </div>

      {checked.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-900 text-white rounded-sm px-4 py-2.5 mb-3" data-testid="bulk-bar">
          <span className="text-sm whitespace-nowrap">{checked.size} SKU{checked.size > 1 ? "s" : ""} selected</span>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={bulkFam} onValueChange={setBulkFam}>
              <SelectTrigger className="rounded-sm w-44 h-9 bg-white text-slate-900" data-testid="bulk-family-select"><SelectValue placeholder="Add to family…" /></SelectTrigger>
              <SelectContent>{families.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" className="rounded-sm bg-teal-600 hover:bg-teal-700 transition-colors" onClick={bulkAssignFamily} data-testid="bulk-assign-family-button">Assign family</Button>
            <Select value={bulkKind} onValueChange={setBulkKind}>
              <SelectTrigger className="rounded-sm w-32 h-9 bg-white text-slate-900" data-testid="bulk-kind-select"><SelectValue placeholder="Set type…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Unit</SelectItem>
                <SelectItem value="1">Accessory</SelectItem>
                <SelectItem value="2">Part</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" className="rounded-sm bg-indigo-600 hover:bg-indigo-700 transition-colors" onClick={bulkSetKind} data-testid="bulk-set-kind-button">Set type</Button>
            <input ref={bulkAttRef} type="file" accept=".pdf,image/*" hidden onChange={(e) => { const f = e.target.files[0]; if (f) { setBulkAttFile(f); setBulkAttName(f.name); } }} data-testid="bulk-attachment-input" />
            <Button size="sm" className="rounded-sm bg-purple-600 hover:bg-purple-700 transition-colors" onClick={() => bulkAttRef.current?.click()} data-testid="bulk-attach-button"><Paperclip className="h-4 w-4 mr-2" /> Attach file to selected</Button>
            <Button size="sm" variant="ghost" className="rounded-sm text-slate-200 hover:text-white hover:bg-slate-800" onClick={openLibrary} data-testid="bulk-library-button"><Paperclip className="h-4 w-4 mr-2" /> From library</Button>
            <Button size="sm" variant="ghost" className="rounded-sm text-slate-200 hover:text-white hover:bg-slate-800" onClick={() => setComboOpen(true)} data-testid="bulk-combo-button">Apply assets</Button>
            <input ref={bulkRef} type="file" accept="image/*" hidden onChange={(e) => bulkApply(e.target.files[0])} data-testid="bulk-image-input" />
            <Button size="sm" className="rounded-sm bg-blue-600 hover:bg-blue-700 transition-colors" onClick={() => bulkRef.current?.click()} data-testid="bulk-apply-image-button">
              <ImageIcon className="h-4 w-4 mr-2" /> Apply image to selected
            </Button>
            <Button size="sm" variant="ghost" className="rounded-sm text-slate-200 hover:text-white hover:bg-slate-800" onClick={() => setNotesOpen(true)} data-testid="bulk-notes-button">Set note</Button>
            {filter === "archived"
              ? <Button size="sm" variant="ghost" className="rounded-sm text-slate-200 hover:text-white hover:bg-slate-800" onClick={() => bulkArchive(false)} data-testid="bulk-restore-button"><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</Button>
              : <Button size="sm" variant="ghost" className="rounded-sm text-slate-200 hover:text-white hover:bg-slate-800" onClick={() => bulkArchive(true)} data-testid="bulk-archive-button"><Archive className="h-4 w-4 mr-2" /> Archive</Button>}
            <Button size="sm" variant="ghost" className="rounded-sm text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => setChecked(new Set())}>Clear</Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 w-10"><Checkbox checked={allVisibleSelected} onCheckedChange={toggleAll} data-testid="select-all-products" /></th>
              <th className="text-left px-4 py-3">SKU</th>
              <th className="text-left px-4 py-3">Description</th>
              <th className="text-left px-4 py-3">Families</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-center px-4 py-3">Assets</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="products-table">
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {!loading && products.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No products. Import a price file first.</td></tr>
            )}
            {products.map((p) => (
              <tr key={p.sku} className="border-t border-slate-100 even:bg-slate-50/50 hover:bg-blue-50/40 transition-colors">
                <td className="px-4 py-2.5"><Checkbox checked={checked.has(p.sku)} onCheckedChange={() => toggleOne(p.sku)} data-testid={`select-${p.sku}`} /></td>
                <td className="px-4 py-2.5 font-mono text-slate-900 whitespace-nowrap">
                  {p.sku}
                  {p.orphaned && <Ghost className="h-3.5 w-3.5 inline ml-2 text-blue-500" title="Orphaned" />}
                  {p.archived && <Archive className="h-3.5 w-3.5 inline ml-2 text-slate-400" title="Archived" data-testid={`archived-icon-${p.sku}`} />}
                </td>
                <td className="px-4 py-2.5 text-slate-600 max-w-md">
                  <div className="whitespace-normal break-words">{p.description}</div>
                  {p.notes ? <div className="text-xs text-slate-400 mt-0.5 whitespace-normal break-words">{p.notes}</div> : null}
                  {p.archived ? <div className="text-xs text-slate-500 mt-0.5 italic" data-testid={`archived-reason-${p.sku}`}>Archived{p.archived_by ? ` by ${p.archived_by}` : ""}{p.archived_reason ? `: ${p.archived_reason}` : ""}</div> : null}
                </td>
                <td className="px-4 py-2.5">
                  {p.families.length === 0
                    ? <Badge variant="outline" className="rounded-sm border-amber-400 text-amber-700 bg-amber-50">Unclassified</Badge>
                    : <div className="flex flex-wrap gap-1">{p.families.map((f) => <Badge key={f} className="rounded-sm bg-slate-900">{f}</Badge>)}</div>}
                </td>
                <td className="px-4 py-2.5">
                  <Badge variant="outline" className={`rounded-sm ${p.kind === 0 ? "border-blue-400 text-blue-700 bg-blue-50" : p.kind === 1 ? "border-teal-400 text-teal-700 bg-teal-50" : "border-slate-300 text-slate-600 bg-slate-50"}`}>{KIND[p.kind ?? 0]}</Badge>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-center gap-3 text-slate-400">
                    <span className={p.has_image ? "text-green-600" : ""}><ImageIcon className="h-4 w-4" /></span>
                    <span className={p.attachments.length ? "text-green-600" : ""}>
                      <span className="inline-flex items-center gap-0.5"><Paperclip className="h-4 w-4" />{p.attachments.length || ""}</span>
                    </span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setSelected(p)} data-testid={`manage-${p.sku}`}>Manage</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {bulkAttFile && (
        <Dialog open onOpenChange={() => setBulkAttFile(null)}>
          <DialogContent className="rounded-sm" data-testid="bulk-rename-dialog">
            <DialogHeader><DialogTitle className="font-heading">Attach to {checked.size} SKU{checked.size === 1 ? "" : "s"}</DialogTitle></DialogHeader>
            <DialogDescription className="sr-only">Name the file customers will see.</DialogDescription>
            <Input value={bulkAttName} onChange={(e) => setBulkAttName(e.target.value)} className="rounded-sm" data-testid="bulk-rename-input" />
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setBulkAttFile(null)}>Cancel</Button>
              <Button className="rounded-sm bg-slate-900" onClick={bulkAttach} data-testid="bulk-rename-confirm">Attach</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="library-dialog">
          <DialogHeader><DialogTitle className="font-heading">Attach from library</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Reuse a previously uploaded file across the selected SKUs.</DialogDescription>
          <div className="max-h-96 overflow-auto divide-y divide-slate-100">
            {libItems.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No files uploaded yet.</div>}
            {libItems.map((it, i) => (
              <div key={it.ref_id || it.storage_path} className="flex items-center gap-2 py-2">
                <Paperclip className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="flex-1 truncate text-sm">{it.filename}</span>
                <Button size="sm" className="rounded-sm bg-slate-900" onClick={() => attachExisting(it)} data-testid={`library-attach-${i}`}>Attach to {checked.size}</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={comboOpen} onOpenChange={setComboOpen}>
        <DialogContent className="rounded-sm" data-testid="combo-dialog">
          <DialogHeader><DialogTitle className="font-heading">Apply assets to {checked.size} SKU{checked.size === 1 ? "" : "s"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Apply an image and/or a document to the selected SKUs at once.</DialogDescription>
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-[0.1em] text-slate-500 mb-1">Product image</div>
              <input type="file" accept="image/*" onChange={(e) => setComboImg(e.target.files[0])} data-testid="combo-image-input" className="text-sm" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.1em] text-slate-500 mb-1">Document</div>
              <input type="file" accept=".pdf,image/*" onChange={(e) => { const f = e.target.files[0]; setComboDoc(f); if (f && !comboDocName) setComboDocName(f.name); }} data-testid="combo-doc-input" className="text-sm" />
            </div>
            {comboDoc && <Input value={comboDocName} onChange={(e) => setComboDocName(e.target.value)} placeholder="Document name" className="rounded-sm" data-testid="combo-doc-name" />}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setComboOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-slate-900" onClick={applyCombo} data-testid="combo-apply">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BlockedSkusDialog open={blockedOpen} onClose={() => setBlockedOpen(false)} onChanged={() => { load(); onChange?.(); }} />
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="rounded-sm" data-testid="bulk-notes-dialog">
          <DialogHeader><DialogTitle className="font-heading">Set note on {checked.size} SKU{checked.size === 1 ? "" : "s"}</DialogTitle></DialogHeader>
          <DialogDescription>Replaces any existing note on the selected products. Leave blank to clear their notes.</DialogDescription>
          <Textarea rows={2} maxLength={500} value={bulkNotesText} onChange={(e) => setBulkNotesText(e.target.value)} placeholder="e.g. Non-stock item — allow 2–3 weeks lead time" className="rounded-sm text-sm" data-testid="bulk-notes-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setNotesOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-slate-900" onClick={bulkNotes} data-testid="bulk-notes-confirm">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {selected && (
        <AssetDialog product={selected} families={families} onClose={() => setSelected(null)}
          onChanged={() => { load(); onChange?.(); }} />
      )}
    </div>
  );
}
